/**
 * `$batch` for the release sitting (13-07, 13-RESEARCH Pattern 4 / Pitfall 3). Pure: no Graph.
 *
 *   - `chunk` cuts the work into Graph's 20-request limit; the chunks run SEQUENTIALLY.
 *   - `buildPatchRequests` gives each row one PATCH with its own `If-Match`.
 *   - `partition` sorts the replies. A `$batch` returns a top-level 200 with failures inside, in
 *     ANY order, so every reply is correlated by `id` and checked. A reply missing its id, or a
 *     request with no reply, is FAILED, never ok.
 *   - `buildAuditRequests` makes the follow-up batch of audit POSTs for the 2xx PATCHes only
 *     (the parent first, then its audit rows: RESEARCH anti-pattern "writing the audit row first").
 *
 * No `dependsOn` anywhere: batches are fully parallel or fully sequential (a failed dependency
 * returns 424, and Microsoft's guidance is not to mix the two).
 */

// ---------------------------------------------------------------------------
// Shapes and header helpers (graphClient imports these; this module imports nothing)
// ---------------------------------------------------------------------------

/** One request inside a JSON `$batch`. Never `dependsOn` (RESEARCH Pitfall 3). */
export interface GraphBatchRequest {
  id: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** Relative to the version root, e.g. `/sites/{siteId}/lists/{listId}/items/{id}/fields`. */
  url: string;
  headers?: Record<string, string>;
  body?: unknown;
}

/** One response inside a `$batch` reply. They come back in ANY order; correlate by `id`. */
export interface GraphBatchResponse {
  id?: string;
  status: number;
  headers?: Record<string, string>;
  body?: unknown;
}

/**
 * Read one header from either a fetch `Headers` object (what the Graph SDK attaches to a
 * `GraphError`) or a plain record (what a `$batch` response carries), case-insensitively.
 */
export function readHeader(headers: unknown, name: string): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined;
  const h = headers as { get?: unknown };
  if (typeof h.get === 'function') {
    const v = (h.get as (n: string) => string | null).call(headers, name);
    return v ?? undefined;
  }
  const want = name.toLowerCase();
  for (const [k, v] of Object.entries(headers as Record<string, unknown>)) {
    if (k.toLowerCase() === want && v !== undefined && v !== null) return String(v);
  }
  return undefined;
}

/**
 * `Retry-After` → milliseconds. Accepts delta-seconds (`"3"`, `"1.5"`) or an HTTP-date.
 * Anything unparseable → undefined (the caller falls back to exponential backoff).
 */
export function parseRetryAfter(value: string | null | undefined, nowMs: number = Date.now()): number | undefined {
  if (value === null || value === undefined) return undefined;
  const v = value.trim();
  if (v === '') return undefined;
  if (/^\d+(\.\d+)?$/.test(v)) return Math.round(Number(v) * 1000);
  const t = Date.parse(v);
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, t - nowMs);
}


/** Graph's limit on requests per `$batch`. */
export const MAX_BATCH = 20;

/** Throttle statuses inside a batch: re-batch after the longest Retry-After. */
export const THROTTLE_STATUSES: readonly number[] = [429, 503];

/** Split `items` into chunks of `size` (default 20), order kept. */
export function chunk<T>(items: readonly T[], size: number = MAX_BATCH): T[][] {
  if (!Number.isInteger(size) || size < 1) throw new RangeError(`chunk: size must be a positive integer, got ${size}`);
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Where a batch's requests point: a list on a site. */
export interface ListTarget {
  siteId: string;
  listId: string;
}

/** What `buildPatchRequests` needs of a row. */
export interface PatchableRow {
  id: number;
  eTag: string;
}

/**
 * One PATCH per row: `id` = the item id as a string (unique per batch, so correlation is exact),
 * `If-Match` = the row's eTag, `Content-Type: application/json`. Rows whose `fieldsFor` is empty
 * are left out (there is nothing to write). A repeated item id throws.
 */
export function buildPatchRequests<R extends PatchableRow>(
  rows: readonly R[],
  fieldsFor: (row: R) => Record<string, unknown>,
  target: ListTarget,
): GraphBatchRequest[] {
  const seen = new Set<string>();
  const out: GraphBatchRequest[] = [];
  for (const row of rows) {
    const id = String(row.id);
    if (seen.has(id)) throw new Error(`buildPatchRequests: item ${id} appears twice in one batch`);
    seen.add(id);
    if (!row.eTag) throw new Error(`buildPatchRequests: item ${id} has no eTag (If-Match is required)`);
    const body = fieldsFor(row);
    if (!body || Object.keys(body).length === 0) continue;
    out.push({
      id,
      method: 'PATCH',
      url: `/sites/${target.siteId}/lists/${target.listId}/items/${row.id}/fields`,
      headers: { 'Content-Type': 'application/json', 'If-Match': row.eTag },
      body,
    });
  }
  return out;
}

/** A request paired with its reply. */
export interface BatchResult {
  request: GraphBatchRequest;
  response: GraphBatchResponse;
}

/** A failure. `request` is absent for a reply that could not be correlated. */
export interface BatchFailure {
  request?: GraphBatchRequest;
  response?: GraphBatchResponse;
  reason: 'no-response' | 'missing-id' | 'unknown-id' | 'duplicate-response' | 'status';
}

export interface BatchPartition {
  /** 2xx. */
  ok: BatchResult[];
  /** 412: the row changed since it was read ("changed by someone else"). */
  conflict412: BatchResult[];
  /** 429 / 503: re-batch, after `retryAfterMs` when known. */
  throttled: {
    items: BatchResult[];
    /** max(Retry-After) across the throttled replies, in ms; undefined when none sent one. */
    retryAfterMs?: number;
  };
  /** Everything else, plus every reply or request that could not be correlated. */
  failed: BatchFailure[];
}

/**
 * Sort a batch's replies by status, correlated by `id`. Every request ends up in exactly one
 * bucket; a request with no reply is failed (`no-response`), and a reply that matches no request
 * (missing id, unknown id, second reply for one id) is failed too, never ok.
 */
export function partition(
  requests: readonly GraphBatchRequest[],
  responses: readonly GraphBatchResponse[],
  nowMs: number = Date.now(),
): BatchPartition {
  const byId = new Map<string, GraphBatchRequest>();
  for (const r of requests) byId.set(r.id, r);
  const answered = new Set<string>();
  const result: BatchPartition = { ok: [], conflict412: [], throttled: { items: [] }, failed: [] };
  let maxRetry: number | undefined;

  for (const response of responses) {
    const id = typeof response.id === 'string' && response.id !== '' ? response.id : undefined;
    if (id === undefined) {
      result.failed.push({ response, reason: 'missing-id' });
      continue;
    }
    const request = byId.get(id);
    if (!request) {
      result.failed.push({ response, reason: 'unknown-id' });
      continue;
    }
    if (answered.has(id)) {
      result.failed.push({ request, response, reason: 'duplicate-response' });
      continue;
    }
    answered.add(id);
    const status = Number(response.status);
    if (status >= 200 && status < 300) {
      result.ok.push({ request, response });
    } else if (status === 412) {
      result.conflict412.push({ request, response });
    } else if (THROTTLE_STATUSES.includes(status)) {
      result.throttled.items.push({ request, response });
      const ms = parseRetryAfter(readHeader(response.headers, 'retry-after'), nowMs);
      if (ms !== undefined) maxRetry = maxRetry === undefined ? ms : Math.max(maxRetry, ms);
    } else {
      result.failed.push({ request, response, reason: 'status' });
    }
  }

  for (const request of requests) {
    if (!answered.has(request.id)) result.failed.push({ request, reason: 'no-response' });
  }
  if (maxRetry !== undefined) result.throttled.retryAfterMs = maxRetry;
  return result;
}

/**
 * The follow-up audit POSTs for the 2xx PATCHes ONLY. `auditFor` returns the audit rows' field
 * bags for one ok result; each becomes a POST with id `<patch id>-a<n>` (unique, and traceable
 * back to its row). The caller chunks the result (one PATCH may carry several audit rows).
 */
export function buildAuditRequests(
  okResults: readonly BatchResult[],
  auditFor: (result: BatchResult) => readonly Record<string, unknown>[],
  target: ListTarget,
): GraphBatchRequest[] {
  const out: GraphBatchRequest[] = [];
  for (const result of okResults) {
    const status = Number(result.response.status);
    if (!(status >= 200 && status < 300)) continue;
    auditFor(result).forEach((fields, n) => {
      out.push({
        id: `${result.request.id}-a${n}`,
        method: 'POST',
        url: `/sites/${target.siteId}/lists/${target.listId}/items`,
        headers: { 'Content-Type': 'application/json' },
        body: { fields },
      });
    });
  }
  return out;
}
